import { Button, ListItemIcon, Menu, MenuItem, MenuList, Tooltip, ListItemText } from "@mui/material";
import { GridRowId } from "@mui/x-data-grid-pro";
import { useEffect, useState } from "react";
import { ContentCopy, ArrowDropDown, LockOpen, LockClock, FileDownload } from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import useFeatureFlag from "hooks/useFeatureFlag";
import { useAlert } from "hooks/alerts/useAlert";
import { api } from "services/api.service";
import { codDownloadParams, downloadBlob, filenameFromContentDisposition } from "./codDownload";

type AssetsDataGridActionsProps = {
  setSecurityLocksOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedAssetIds: GridRowId[];
  getAllSerialsNumbers: (asset_ids?: GridRowId[], selectAllFlag?: boolean) => Promise<void>;
  selectAllFlag?: boolean;
  setNotFoundOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function AssetsDataGridActions({
  setSecurityLocksOpen,
  selectedAssetIds,
  getAllSerialsNumbers,
  selectAllFlag,
  setNotFoundOpen,
}: AssetsDataGridActionsProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [downloadingCods, setDownloadingCods] = useState(false);
  const open = Boolean(anchorEl);
  const { search } = useLocation();
  const { enabled: isEnrollmentV2Enabled } = useFeatureFlag("enrollment_v2");
  const { alertError } = useAlert();
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const isEnrolled = search && search.includes("status%2Cis%2CEnrolled");

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCopySerials = () => {
    getAllSerialsNumbers(selectedAssetIds, selectAllFlag).then(() => {
      handleClose();
    });
  };

  const handleDownloadCods = () => {
    if (downloadingCods) return;

    setDownloadingCods(true);
    const params = codDownloadParams(search, selectedAssetIds, Boolean(selectAllFlag));
    api
      .get<Blob>(`/assets/certificates_of_destruction?${params.toString()}`, { responseType: "blob" })
      .then(({ data, headers }) => {
        downloadBlob(data, filenameFromContentDisposition(headers["content-disposition"]));
        handleClose();
      })
      .catch((error) => {
        if (error?.response?.status === 404) {
          alertError("None of the selected assets have a certificate of destruction.");
        } else {
          alertError("Could not download certificates. Please try again.");
        }
      })
      .finally(() => setDownloadingCods(false));
  };

  useEffect(() => {
    setAnchorEl(null);
  }, [selectedAssetIds]);

  return (
    <>
      <Tooltip title="Select assets to perform bulk actions">
        <span>
          <Button
            onClick={handleClick}
            variant="contained"
            disabled={!selectedAssetIds.length}
            endIcon={<ArrowDropDown />}
          >
            Actions
          </Button>
        </span>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            width: "20ch",
          },
        }}
      >
        {
          <MenuList>
            <MenuItem
              onClick={(e) => {
                setSecurityLocksOpen(true);
              }}
            >
              <ListItemIcon>
                <LockOpen fontSize="small" />
              </ListItemIcon>
              <ListItemText>Confirm released</ListItemText>
            </MenuItem>
            {isEnrollmentV2Enabled && (
              <MenuItem onClick={() => setNotFoundOpen(true)}>
                <ListItemIcon>
                  <LockClock fontSize="small" />
                </ListItemIcon>
                <ListItemText>Can't release?</ListItemText>
              </MenuItem>
            )}
            {isEnrolled && (
              <MenuItem onClick={handleCopySerials}>
                <ListItemIcon>
                  <ContentCopy fontSize="small" />
                </ListItemIcon>
                <ListItemText>Copy serials</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={handleDownloadCods} disabled={downloadingCods}>
              <ListItemIcon>
                <FileDownload fontSize="small" />
              </ListItemIcon>
              <ListItemText>{downloadingCods ? "Downloading CODs" : "Download CODs"}</ListItemText>
            </MenuItem>
          </MenuList>
        }
      </Menu>
    </>
  );
}
